<template>
  <div class="coming-soon">
    <div class="coming-soon__content">
      <div class="coming-soon__pill">
        {{ $t("broker.common.comingSoon") }}
      </div>

      <h1 class="coming-soon__title">{{ title }}</h1>

      <div class="coming-soon__image-wrap">
        <img class="coming-soon__image" :src="imageSrc" :alt="title" />
      </div>

      <p class="coming-soon__desc">
        {{ description }}
      </p>

      <div
        v-if="!canCheckExistingVote || existingVoteLoaded === null"
        class="coming-soon__loading-placeholder"
      ></div>

      <div
        v-else-if="existingVote"
        :class="[
          'coming-soon__already-voted',
          existingVote.voteType === 'NOT_INTERESTED'
            ? 'coming-soon__already-voted--neutral'
            : 'coming-soon__already-voted--positive',
        ]"
      >
        {{ alreadyVotedMessage }}
      </div>

      <div v-else class="coming-soon__actions">
        <v-btn class="btn-secondary" @click="openVoteModal('NOT_INTERESTED')">
          {{ $t("broker.common.notInterested") }}
        </v-btn>

        <v-btn class="btn-primary" @click="openVoteModal('INTERESTED')">
          <v-icon left small>fa-regular fa-thumbs-up</v-icon>
          {{ $t("broker.common.looksGreat") }}
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="voteDialog" max-width="560">
      <v-card class="vote-modal">
        <v-card-title class="vote-modal__title">
          {{ $t("broker.common.shareYourFeedback") }}
        </v-card-title>

        <v-card-text>
          <div class="vote-modal__subtitle">
            {{
              selectedVoteType === "INTERESTED"
                ? $t("broker.common.interestedFeedbackDescription")
                : $t("broker.common.notInterestedFeedbackDescription")
            }}
          </div>

          <v-textarea
            v-model="voteDescription"
            :label="$t('broker.common.description')"
            :placeholder="$t('broker.common.descriptionPlaceholder')"
            outlined
            rows="4"
            auto-grow
            counter="500"
            maxlength="500"
          />
        </v-card-text>

        <v-card-actions class="justify-end">
          <button text @click="closeVoteModal">
            {{ $t("broker.common.cancel") }}
          </button>

          <v-btn
            class="btn-primary"
            :loading="submittingVote"
            :disabled="submittingVote || !voteDescription.trim()"
            @click="submitVote"
          >
            {{ $t("broker.common.submit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import featureVotesService from "../services/feature-votes.service";

export default {
  name: "ComingSoonFeatureVote",

  props: {
    title: { type: String, required: true },
    description: { type: String, required: true },
    imageSrc: { type: String, required: true },
    featureName: { type: String, required: true },
  },

  data() {
    return {
      voteDialog: false,
      selectedVoteType: null,
      voteDescription: "",
      submittingVote: false,
      existingVote: null,
      loadingExistingVote: false,
      existingVoteLoaded: null,
    };
  },

  computed: {
    currentUserId() {
      return this.$store.getters["broker/currentUser"]?.id || null;
    },

    alreadyVotedMessage() {
      if (!this.existingVote || !this.existingVote.voteType) return "";

      const option =
        this.existingVote.voteType === "INTERESTED"
          ? this.$t("broker.common.looksGreat")
          : this.$t("broker.common.notInterested");

      return this.$t("broker.common.alreadyVotedMessage", { option });
    },
    canCheckExistingVote() {
      return !!this.currentUserId && !!this.featureName;
    },
  },

  watch: {
    featureName: {
      immediate: true,
      handler() {
        this.fetchExistingVote();
      },
    },

    "$route.path": {
      immediate: true,
      handler() {
        this.fetchExistingVote();
      },
    },

    currentUserId: {
      immediate: true,
      handler() {
        this.fetchExistingVote();
      },
    },
  },

  methods: {
    async fetchExistingVote() {
      const userId = this.currentUserId;

      if (!userId || !this.featureName) {
        this.loadingExistingVote = false;
        this.existingVote = null;
        this.existingVoteLoaded = null;
        return;
      }

      try {
        this.loadingExistingVote = true;
        this.existingVoteLoaded = null;
        this.existingVote = null;

        const response =
          await featureVotesService.getFeatureVoteByUserAndFeature(
            userId,
            this.featureName,
          );

        this.existingVote = response;
      } catch (error) {
        this.existingVote = null;
      } finally {
        this.loadingExistingVote = false;
        this.existingVoteLoaded = true;
      }
    },

    openVoteModal(voteType) {
      this.selectedVoteType = voteType;
      this.voteDescription = "";
      this.voteDialog = true;
    },

    closeVoteModal() {
      this.voteDialog = false;
      this.selectedVoteType = null;
      this.voteDescription = "";
    },

    async submitVote() {
      if (!this.selectedVoteType || !this.voteDescription.trim()) {
        return;
      }

      try {
        this.submittingVote = true;

        await featureVotesService.createFeatureVote({
          featureName: this.featureName,
          voteType: this.selectedVoteType,
          description: this.voteDescription.trim(),
        });

        this.existingVote = {
          featureName: this.featureName,
          voteType: this.selectedVoteType,
          description: this.voteDescription.trim(),
        };

        this.closeVoteModal();

        if (this.$toast?.success) {
          this.$toast.success(this.$t("broker.common.voteSubmitted"));
        }
      } catch (error) {
        console.error("Error submitting vote:", error);

        if (this.$toast?.error) {
          this.$toast.error(this.$t("broker.common.voteSubmitError"));
        }
      } finally {
        this.submittingVote = false;
      }
    },
  },
};
</script>

<style scoped>
.coming-soon {
  width: 100%;
  height: calc(100vh - 48px - 24px);
  display: flex;
  align-items: center;
}

.coming-soon__content {
  width: 100%;
  max-width: 656px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  gap: 15px;
  flex-direction: column;
  align-items: center;
}

.coming-soon__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(0, 120, 80, 0.45);
  color: #0b6b4f;
  font-size: 14px;
  font-weight: 600;
}

.coming-soon__title {
  margin: 0 0 18px 0;
  font-size: 41px;
  line-height: 1.1;
  font-weight: 800;
  color: #3b3b3b !important;
}

.coming-soon__image-wrap {
  width: 190px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.coming-soon__image {
  width: 190px;
  height: 200px;
  object-fit: contain;
}

.coming-soon__desc {
  margin: 0 0 18px 0;
  font-size: 16px;
  line-height: 1.6;
  color: #3b3b3b !important;
}

.coming-soon__already-voted {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  text-align: center;
}

.coming-soon__already-voted--positive {
  background-color: #EEEEEE;
  color: #2e7d32;
}

.coming-soon__already-voted--neutral {
  background-color: #f1f3f5;
}

.coming-soon__actions {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
}

::v-deep .v-btn.btn-secondary.v-btn:not(.v-btn--disabled) {
  background-color: #ebebeb !important;
  color: #14161b !important;
}

.btn-secondary {
  min-width: 140px;
  height: 36px;
  box-shadow: none !important;
}

.btn-primary {
  min-width: 140px;
  height: 36px;
  text-transform: none;
  box-shadow: none !important;
}

.vote-modal__title {
  font-weight: 700;
}

.vote-modal__subtitle {
  margin-bottom: 16px;
  color: #666;
}

.coming-soon__loading-placeholder {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
